import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import { requireAccess } from '../lib/guards';
import { formatGermanDateFromTimestamp } from '../lib/date-format';
import { UST_SATZ_DEFAULT } from '../lib/steuer-konstanten';
import {
  BausteinCard,
  bausteinFromApi,
  buildBausteinPayload,
  emptyBaustein,
  type BausteinForm,
} from '../components/vertraege/BausteinEditor';
import {
  buildBeschreibungText,
  ModulDetailCard,
  type ModulFormState,
} from '../components/vertraege/ModulDetailCard';
import { parseBeschreibungTextZuWizardState } from '../lib/bv-wizard-state-parser';
import { bvModulIdValues, MODUL_TEMPLATES, type BvModulId } from '../lib/bv-modul-templates';
import {
  addDecimals,
  formatEuroDe,
  multiplyByInteger,
  parseMoneyOr,
  ZERO_MONEY,
} from '../lib/money-display';
import { Button } from '../components/ui/Button';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { Input } from '../components/ui/Input';
import { PageHeader } from '../components/ui/PageHeader';
import type { EntwurfStatus } from '../lib/api-enums';
import {
  ENTWURF_STATUS_LABELS as STATUS_LABELS,
  ENTWURF_STATUS_BADGE as STATUS_BADGE,
} from '../lib/api-labels';

interface Entwurf {
  id: string;
  kundeId: string;
  objektId: string;
  status: EntwurfStatus;
  entNummer: string | null;
  notizen: string | null;
  createdAt: string;
}

interface Version {
  id: string;
  entwurfId: string;
  versionNr: number;
  istAktiv: boolean;
  versendetAm: string | null;
  versendetAnEmail: string | null;
  pdfStorageKey: string | null;
}

export interface Baustein {
  id: string;
  versionId: string;
  position: number;
  bezeichnung: string;
  beschreibungText: string | null;
  kalkulationstyp: string;
  monatsbetragNetto: string;
  jahresbetragNetto: string;
  subName: string | null;
  modulId: string | null;
  pauschalMonatsbetrag: string | null;
}

function isBvModulId(s: string | null | undefined): s is BvModulId {
  if (s == null) return false;
  return (bvModulIdValues as readonly string[]).includes(s);
}

interface Kunde {
  id: string;
  firmenname: string;
  kundennummer: string;
}

interface Objekt {
  id: string;
  bezeichnung: string;
}

export const Route = createFileRoute('/vertraege/entwurf/$id')({
  beforeLoad: () => requireAccess('/api/v1/vertraege/entwuerfe'),
  component: EntwurfDetailPage,
});

function EntwurfDetailPage() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [aktivierenOpen, setAktivierenOpen] = useState(false);
  const [neueVersionOpen, setNeueVersionOpen] = useState(false);

  const detailQ = useQuery({
    queryKey: ['vertraege', 'entwurf', id],
    queryFn: () =>
      api<{ entwurf: Entwurf; version: Version | null; bausteine: Baustein[] }>(
        `/api/v1/vertraege/entwuerfe/${id}`,
      ),
  });

  const kundenQ = useQuery({
    queryKey: ['kunden', 'lookup'],
    queryFn: () => api<{ items: Kunde[] }>('/api/v1/kunden'),
  });
  const objekteQ = useQuery({
    queryKey: ['objekte', 'lookup'],
    queryFn: () => api<{ items: Objekt[] }>('/api/v1/objekte'),
  });

  const versendeMut = useMutation({
    mutationFn: () =>
      api(`/api/v1/vertraege/entwuerfe/${id}/versende`, { method: 'POST', body: {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vertraege', 'entwurf', id] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });
  const akzeptierenMut = useMutation({
    mutationFn: () =>
      api(`/api/v1/vertraege/entwuerfe/${id}/akzeptieren`, { method: 'POST', body: {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vertraege', 'entwurf', id] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });
  const ablehnenMut = useMutation({
    mutationFn: (grund: string) =>
      api(`/api/v1/vertraege/entwuerfe/${id}/ablehnen`, {
        method: 'POST',
        body: grund ? { grund } : {},
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vertraege', 'entwurf', id] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });

  const neueVersionMut = useMutation({
    mutationFn: (body: { bausteine: ReturnType<typeof buildBausteinPayload>[] }) =>
      api(`/api/v1/vertraege/entwuerfe/${id}/versionen`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      setNeueVersionOpen(false);
      qc.invalidateQueries({ queryKey: ['vertraege', 'entwurf', id] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });

  const aktiviereMut = useMutation({
    mutationFn: (body: {
      unterschriebenesPdfStorageKey: string;
      unterschriebenAm: string;
      laufzeitVon: string;
      zahlungszielTage?: number;
      ustSatz?: string;
    }) =>
      api<{ bv: { id: string } }>(`/api/v1/vertraege/entwuerfe/${id}/aktivieren`, {
        method: 'POST',
        body,
      }),
    onSuccess: (r) => {
      void navigate({ to: '/vertraege/bv/$id', params: { id: r.bv.id } });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });

  if (detailQ.isLoading) return <div className="p-4 text-sm text-slate-500">Lade…</div>;
  if (!detailQ.data)
    return <div className="p-4 text-sm text-slate-500">Entwurf nicht gefunden.</div>;

  const { entwurf, version, bausteine } = detailQ.data;
  const kunde = (kundenQ.data?.items ?? []).find((k) => k.id === entwurf.kundeId);
  const objekt = (objekteQ.data?.items ?? []).find((o) => o.id === entwurf.objektId);

  const monatsSum = bausteine.reduce(
    (s, b) => addDecimals(s, parseMoneyOr(b.monatsbetragNetto, ZERO_MONEY)),
    ZERO_MONEY,
  );
  const quartalsSum = multiplyByInteger(monatsSum, 3);
  const jahresSum = multiplyByInteger(monatsSum, 12);

  return (
    <div className="space-y-4">
      <PageHeader
        title={`Vertragsentwurf ${entwurf.entNummer ?? entwurf.id.slice(0, 8)}`}
        description={`${kunde?.firmenname ?? '—'} / ${objekt?.bezeichnung ?? '—'}`}
        actions={
          <Link to="/vertraege" className="text-sm text-slate-600 hover:text-slate-800">
            ← zurück zur Liste
          </Link>
        }
      />

      {error ? <ErrorBanner error={error} /> : null}

      <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <span className="font-medium text-slate-700">Status:</span>
          <span
            className={`inline-flex rounded-md border px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[entwurf.status]}`}
          >
            {STATUS_LABELS[entwurf.status]}
          </span>
          {version ? (
            <span className="text-xs text-slate-500">
              Version v{version.versionNr}
              {version.versendetAm
                ? ` · versendet ${formatGermanDateFromTimestamp(version.versendetAm)}`
                : ''}
            </span>
          ) : (
            <span className="text-xs text-amber-700">Noch keine Version (leerer Entwurf)</span>
          )}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {entwurf.status === 'entwurf' && version ? (
            <Button onClick={() => versendeMut.mutate()} disabled={versendeMut.isPending}>
              Versenden
            </Button>
          ) : null}
          {entwurf.status === 'versendet' ? (
            <>
              <Button onClick={() => akzeptierenMut.mutate()} disabled={akzeptierenMut.isPending}>
                Akzeptieren
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  const grund = window.prompt('Grund für Ablehnung (optional):');
                  if (grund !== null) ablehnenMut.mutate(grund);
                }}
                disabled={ablehnenMut.isPending}
              >
                Ablehnen
              </Button>
            </>
          ) : null}
          {entwurf.status === 'akzeptiert' ? (
            <Button onClick={() => setAktivierenOpen(true)} disabled={aktiviereMut.isPending}>
              BV aktivieren
            </Button>
          ) : null}
          {(entwurf.status === 'entwurf' || entwurf.status === 'versendet') &&
          bausteine.length > 0 ? (
            <Button variant="ghost" onClick={() => setNeueVersionOpen(true)}>
              Neue Version
            </Button>
          ) : null}
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="mb-3 text-sm font-semibold text-slate-700">
          Bausteine ({bausteine.length})
        </h2>
        <table className="min-w-full divide-y divide-slate-200">
          <thead>
            <tr className="text-left text-xs font-medium uppercase tracking-wide text-slate-500">
              <th className="px-2 py-2">#</th>
              <th className="px-2 py-2">Bezeichnung</th>
              <th className="px-2 py-2">Kalkulationstyp</th>
              <th className="px-2 py-2 text-right">Monat €</th>
              <th className="px-2 py-2 text-right">Jahr €</th>
              <th className="px-2 py-2">Subunternehmer</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 text-sm">
            {bausteine.map((b) => (
              <tr key={b.id}>
                <td className="px-2 py-1.5 font-mono text-slate-500">{b.position}</td>
                <td className="px-2 py-1.5 text-slate-700">{b.bezeichnung}</td>
                <td className="px-2 py-1.5 text-xs text-slate-500">{b.kalkulationstyp}</td>
                <td className="px-2 py-1.5 text-right font-mono text-slate-700">
                  {b.monatsbetragNetto}
                </td>
                <td className="px-2 py-1.5 text-right font-mono text-slate-700">
                  {b.jahresbetragNetto}
                </td>
                <td className="px-2 py-1.5 text-xs text-slate-500">{b.subName ?? '—'}</td>
              </tr>
            ))}
            {bausteine.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-2 py-4 text-center text-sm text-slate-500">
                  Keine Bausteine — Entwurf ist leer.
                </td>
              </tr>
            ) : null}
          </tbody>
          {bausteine.length > 0 ? (
            <tfoot>
              <tr className="bg-slate-50 text-xs font-medium text-slate-600">
                <td colSpan={3} className="px-2 py-2 text-right">
                  Summen netto:
                </td>
                <td className="px-2 py-2 text-right font-mono">{formatEuroDe(monatsSum)} / Mon</td>
                <td className="px-2 py-2 text-right font-mono">{formatEuroDe(jahresSum)} / J</td>
                <td className="px-2 py-2 text-right text-xs text-slate-500">
                  Quartal: {formatEuroDe(quartalsSum)}
                </td>
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>

      {entwurf.notizen ? (
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <h2 className="mb-2 text-sm font-semibold text-slate-700">Notizen (intern)</h2>
          <p className="whitespace-pre-wrap text-sm text-slate-600">{entwurf.notizen}</p>
        </div>
      ) : null}

      {aktivierenOpen ? (
        <AktiviereBvModal
          onClose={() => setAktivierenOpen(false)}
          onSubmit={(body) => {
            setAktivierenOpen(false);
            aktiviereMut.mutate(body);
          }}
        />
      ) : null}

      {neueVersionOpen ? (
        <NeueVersionModal
          initial={bausteine}
          onClose={() => setNeueVersionOpen(false)}
          onSubmit={(payload) => neueVersionMut.mutate({ bausteine: payload })}
          submitting={neueVersionMut.isPending}
        />
      ) : null}
    </div>
  );
}

type ModalEntry =
  | {
      mode: 'wizard';
      modulId: BvModulId;
      bezeichnung: string;
      wizardState: ModulFormState;
    }
  | {
      mode: 'fallback';
      form: BausteinForm;
    };

function bausteinToModalEntry(b: Baustein): ModalEntry {
  if (isBvModulId(b.modulId)) {
    return {
      mode: 'wizard',
      modulId: b.modulId,
      bezeichnung: b.bezeichnung,
      wizardState: parseBeschreibungTextZuWizardState(
        b.beschreibungText,
        b.modulId,
        b.pauschalMonatsbetrag ?? '',
      ),
    };
  }
  return {
    mode: 'fallback',
    form: bausteinFromApi(b as unknown as Record<string, unknown>),
  };
}

function buildEntryPayload(entry: ModalEntry, position: number): Record<string, unknown> {
  if (entry.mode === 'fallback') {
    return { ...buildBausteinPayload(entry.form), position };
  }
  const tpl = MODUL_TEMPLATES[entry.modulId];
  const beschreibungText = buildBeschreibungText(
    entry.modulId,
    entry.wizardState.selectedPositionIds,
    entry.wizardState.positionFrequenzen,
    entry.wizardState.freitextItems,
  );
  const monatsbetrag = parseMoneyOr(entry.wizardState.pauschalMonatsbetrag, ZERO_MONEY);
  const out: Record<string, unknown> = {
    position,
    bezeichnung: entry.bezeichnung,
    kalkulationstyp: tpl.defaultKalkulationstyp,
    pauschalMonatsbetrag: monatsbetrag,
    monatsbetragNetto: monatsbetrag,
    jahresbetragNetto: multiplyByInteger(monatsbetrag, 12),
    modulId: entry.modulId,
  };
  if (beschreibungText.length > 0) out['beschreibungText'] = beschreibungText;
  return out;
}

export function NeueVersionModal({
  initial,
  onClose,
  onSubmit,
  submitting,
}: {
  initial: Baustein[];
  onClose: () => void;
  onSubmit: (bausteine: Record<string, unknown>[]) => void;
  submitting: boolean;
}) {
  const [entries, setEntries] = useState<ModalEntry[]>(
    initial.length > 0
      ? initial.map(bausteinToModalEntry)
      : [{ mode: 'fallback', form: emptyBaustein(1) }],
  );

  function updateWizard(idx: number, patch: Partial<ModulFormState>): void {
    setEntries((arr) =>
      arr.map((e, i) => {
        if (i !== idx || e.mode !== 'wizard') return e;
        return {
          ...e,
          wizardState: { ...e.wizardState, ...patch, initialized: true },
        };
      }),
    );
  }

  function updateForm(idx: number, patch: Partial<BausteinForm>): void {
    setEntries((arr) =>
      arr.map((e, i) => {
        if (i !== idx || e.mode !== 'fallback') return e;
        return { ...e, form: { ...e.form, ...patch } };
      }),
    );
  }

  function add(): void {
    setEntries((arr) => [...arr, { mode: 'fallback', form: emptyBaustein(arr.length + 1) }]);
  }

  function remove(idx: number): void {
    setEntries((arr) => arr.filter((_, i) => i !== idx));
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/40 p-4 pt-10">
      <div className="w-full max-w-3xl rounded-lg bg-white p-5 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Neue Version anlegen</h2>
            <p className="text-xs text-slate-500">
              Bausteine sind aus der aktuellen Version vorbefüllt — bearbeiten und speichern erzeugt
              eine neue aktive Version. Vorherige Version bleibt erhalten.
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded text-sm text-slate-400 hover:text-slate-600"
            aria-label="Schließen"
          >
            ✕
          </button>
        </div>

        <div className="space-y-3">
          {entries.map((entry, idx) => (
            <ModalEntryEditor
              key={idx}
              entry={entry}
              showRemove={entries.length > 1}
              onRemove={() => remove(idx)}
              onWizardChange={(patch) => updateWizard(idx, patch)}
              onFormChange={(patch) => updateForm(idx, patch)}
            />
          ))}
        </div>

        <div className="mt-4 flex justify-between">
          <Button variant="ghost" onClick={add}>
            + Baustein
          </Button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              Abbrechen
            </Button>
            <Button
              onClick={() => onSubmit(entries.map((e, idx) => buildEntryPayload(e, idx + 1)))}
              disabled={submitting}
            >
              {submitting ? 'Speichere…' : 'Neue Version speichern'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ModalEntryEditor({
  entry,
  showRemove,
  onRemove,
  onWizardChange,
  onFormChange,
}: {
  entry: ModalEntry;
  showRemove: boolean;
  onRemove: () => void;
  onWizardChange: (patch: Partial<ModulFormState>) => void;
  onFormChange: (patch: Partial<BausteinForm>) => void;
}) {
  if (entry.mode === 'fallback') {
    const cardProps: React.ComponentProps<typeof BausteinCard> = {
      baustein: entry.form,
      onChange: onFormChange,
    };
    if (showRemove) cardProps.onRemove = onRemove;
    return <BausteinCard {...cardProps} />;
  }

  const { wizardState, modulId } = entry;

  function togglePosition(positionId: string): void {
    const nextSelected = new Set(wizardState.selectedPositionIds);
    const nextFrequenzen = { ...wizardState.positionFrequenzen };
    if (nextSelected.has(positionId)) {
      nextSelected.delete(positionId);
    } else {
      nextSelected.add(positionId);
      // Re-select fällt automatisch auf Katalog-Default-Frequenz zurück, falls
      // wir noch keine Frequenz für diese Position haben (z.B. ursprünglich
      // nicht im Text aufgetaucht).
    }
    onWizardChange({ selectedPositionIds: nextSelected, positionFrequenzen: nextFrequenzen });
  }

  function setPositionFrequenz(
    positionId: string,
    patch: Partial<ModulFormState['positionFrequenzen'][string]>,
  ): void {
    const current = wizardState.positionFrequenzen[positionId] ?? { count: 1, period: 'monthly' };
    const merged = { ...current, ...patch };
    if (merged.period === 'asNeeded' || merged.period === 'ongoing' || merged.period === 'agreed') {
      merged.count = 1;
    }
    onWizardChange({
      positionFrequenzen: { ...wizardState.positionFrequenzen, [positionId]: merged },
    });
  }

  function addFreitext(): void {
    onWizardChange({
      freitextItems: [
        ...wizardState.freitextItems,
        { text: '', frequenz: { count: 1, period: 'monthly' } },
      ],
    });
  }

  function updateFreitextText(idx: number, value: string): void {
    const next = wizardState.freitextItems.slice();
    const target = next[idx];
    if (!target) return;
    next[idx] = { ...target, text: value };
    onWizardChange({ freitextItems: next });
  }

  function setFreitextFrequenz(
    idx: number,
    patch: Partial<ModulFormState['freitextItems'][number]['frequenz']>,
  ): void {
    const next = wizardState.freitextItems.slice();
    const target = next[idx];
    if (!target) return;
    const merged = { ...target.frequenz, ...patch };
    if (merged.period === 'asNeeded' || merged.period === 'ongoing' || merged.period === 'agreed') {
      merged.count = 1;
    }
    next[idx] = { ...target, frequenz: merged };
    onWizardChange({ freitextItems: next });
  }

  function removeFreitext(idx: number): void {
    onWizardChange({ freitextItems: wizardState.freitextItems.filter((_, i) => i !== idx) });
  }

  return (
    <div className="space-y-2">
      {showRemove ? (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onRemove}
            className="text-xs text-red-600 hover:text-red-800"
          >
            Entfernen
          </button>
        </div>
      ) : null}
      <ModulDetailCard
        showAutoPricing={false}
        modulId={modulId}
        state={wizardState}
        onTogglePosition={togglePosition}
        onSetPositionFrequenz={setPositionFrequenz}
        onAddFreitext={addFreitext}
        onUpdateFreitextText={updateFreitextText}
        onSetFreitextFrequenz={setFreitextFrequenz}
        onRemoveFreitext={removeFreitext}
        onChangePauschal={(v) => onWizardChange({ pauschalMonatsbetrag: v })}
      />
    </div>
  );
}

function AktiviereBvModal({
  onClose,
  onSubmit,
}: {
  onClose: () => void;
  onSubmit: (body: {
    unterschriebenesPdfStorageKey: string;
    unterschriebenAm: string;
    laufzeitVon: string;
    zahlungszielTage?: number;
    ustSatz?: string;
  }) => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [pdfKey, setPdfKey] = useState('');
  const [unterschriebenAm, setUnterschriebenAm] = useState(today);
  const [laufzeitVon, setLaufzeitVon] = useState(today);
  const [zahlungszielTage, setZahlungszielTage] = useState('14');
  const [ustSatz, setUstSatz] = useState(UST_SATZ_DEFAULT);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="w-full max-w-lg rounded-lg bg-white p-5 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold text-slate-900">Betreuungsvertrag aktivieren</h2>
          <button
            onClick={onClose}
            className="rounded text-sm text-slate-400 hover:text-slate-600"
            aria-label="Schließen"
          >
            ✕
          </button>
        </div>
        <p className="mb-4 text-xs text-slate-500">
          Erzeugt einen aktiven BV mit BV-Nummer und Quartals-RE-Lauf-Anbindung. Der Entwurf bleibt
          erhalten.
        </p>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-700">
              Unterschriebenes PDF (Storage-Key) *
            </label>
            <Input
              value={pdfKey}
              onChange={(e) => setPdfKey(e.target.value)}
              placeholder="z.B. vertraege/2026/BV-2026-0001.pdf"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-700">
                Unterschrieben am *
              </label>
              <Input
                type="date"
                value={unterschriebenAm}
                onChange={(e) => setUnterschriebenAm(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700">Laufzeit ab *</label>
              <Input
                type="date"
                value={laufzeitVon}
                onChange={(e) => setLaufzeitVon(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700">
                Zahlungsziel (Tage)
              </label>
              <Input
                type="number"
                value={zahlungszielTage}
                onChange={(e) => setZahlungszielTage(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700">USt-Satz (%)</label>
              <Input value={ustSatz} onChange={(e) => setUstSatz(e.target.value)} />
            </div>
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            onClick={() => {
              if (!pdfKey || !unterschriebenAm || !laufzeitVon) return;
              const body: Parameters<typeof onSubmit>[0] = {
                unterschriebenesPdfStorageKey: pdfKey,
                unterschriebenAm,
                laufzeitVon,
              };
              if (zahlungszielTage) body.zahlungszielTage = Number.parseInt(zahlungszielTage, 10);
              if (ustSatz) body.ustSatz = ustSatz;
              onSubmit(body);
            }}
            disabled={!pdfKey || !unterschriebenAm || !laufzeitVon}
          >
            BV aktivieren
          </Button>
        </div>
      </div>
    </div>
  );
}
